/**
 * @module Channels
 */

import { Subject, Subscription, filter } from "rxjs";
import { ErrorHelperService, FileDocumentService, LoggerService, MainService } from 'rainbow-web-sdk';
import { User } from 'rainbow-web-sdk/models/user.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { CHANNEL_SVC, ChannelGetUsersResults, ChannelService } from "rainbow-web-sdk/services/channels/channel.service";
import { CONTACT_SVC, ContactService } from "rainbow-web-sdk/services/core/contact.service";
import { FILEDOCUMENT_SVC } from "../services/fileDocument/fileDocument.service";
import { CHANNEL_POST_MAX_ATTACHMENTS, CHANNEL_POST_MAX_IMAGES, ChannelMessage, ChannelPost, ChannelPostPublication } from "./channelMessage.model";
import { FileDocument } from "./fileDocument.model";

/**
 * @internal
 */
const NB_USERS_TO_GET_DEF = 20;
const NB_USERS_TO_GET_MAX = 100;
const NB_POSTS_TO_LOAD_DEF = 10;
const NB_POSTS_TO_LOAD_MAX = 100;

/**
 * @internal
 * @eventProperty
 * Channel model internal events send to other service/component via sendInnerEvent()
 * Should not be used by SDK user
 */
export enum ChannelInnerEvents {

    /**
     * @eventProperty
     * This RB event is sent when the avatar of the channel may have been modified.
     */
    ON_CHANNEL_AVATAR_UPDATED = "ON_CHANNEL_AVATAR_UPDATED",
}

/**
 * @eventProperty
 * Channel instance specific events.
 * Can listen to it via `Channel.subscribe()` API
 */
export enum ChannelEvents {

    /**
     * @eventProperty
     * This `RBEvent` event is sent when the main information of the channel have been updated.
     * This includes its settings (name, category, topic, number of posts) and avatar.
     */
    ON_CHANNEL_INFORMATION_UPDATED = "ON_CHANNEL_INFORMATION_UPDATED",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` event is sent when channel notifications have been turned on (enabled) by the connected user.
     */
    ON_CHANNEL_NOTIFICATIONS_ON = "ON_CHANNEL_NOTIFICATIONS_ON",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` event is sent when channel notifications have been turned off (disabled) by the connected user.
     */
    ON_CHANNEL_NOTIFICATIONS_OFF = "ON_CHANNEL_NOTIFICATIONS_OFF",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when the connected user has subscribed to a channel.
     */
    ON_CHANNEL_SUBSCRIPTION = "ON_CHANNEL_SUBSCRIPTION",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when the connected user has unsubscribed from a channel.
     */
    ON_CHANNEL_UNSUBSCRIPTION = "ON_CHANNEL_UNSUBSCRIPTION",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when additional posts have been loaded for the channel .
     */
    ON_CHANNEL_POSTS_LOADED = "ON_CHANNEL_POSTS_LOADED",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when a new post has been published in the channel.
     * **Event Data**
     * - **post**: The new published post.
     */
    ON_POST_PUBLISHED = "ON_POST_PUBLISHED",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when a post has been removed from the channel.
     * **Event Data**
     * - **postId**: The removed post id.
     */
    ON_POST_REMOVED = "ON_POST_REMOVED",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` event is sent when all comments associated to a channel post haven been retrieved.
     * PMA OPEN POINT: Shouldn't this event be sent to the ChannelPost interface?
     */
    ON_CHANNEL_COMMENTS_RETRIEVED = "ON_CHANNEL_COMMENTS_RETRIEVED",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` event is sent when a comment has been added to a post of the channel.
     * PMA OPEN POINT: Shouldn't this event be sent to the ChannelPost interface?
     */
    ON_CHANNEL_COMMENT_ADDED = "ON_CHANNEL_COMMENT_ADDED"
}

/**
 * Channel visibility
 */
export enum ChannelVisibility {
    /**
     * Channel visibility limited to the users of my company only.
     * This type of channel is dedicated to your Company's internal communication.
     * Depending on the purpose, you can choose a confidentiality in order to limit the audience of the channel.
     */
    MY_COMPANY = "company",

    /**
     * Channel visibility for all Rainbow users (users of my company and other companies).
     * This type of channel is dedicated to your Company's external communication.
     * The ability for a user to create a channel with this visibility is subject to user right managed by the Company Administrator.
     * The role "public_channels_admin" must be assigned to the user who wants to create this type of channel.
     */
    ALL_USERS = "all"
}

/**
 * Channel confidentiality
 */
export enum ChannelConfidentiality {
    /**
     * Any member of your company and, according to the channel visibility, any Rainbow users can view and subscribe to the channel.
     * Users can also be invited directly by the channel owner at any time.
     * Users can unsubscribe at any time.
     */
    PUBLIC = "public",

    /**
     * The channel can only be followed through an invitation sent by the owner of the channel.
     * Only invited people can consult the channel, they can unsubscribe at any time.
     */
    PRIVATE = "private",

    /**
     * Only selected or all users belonging to the same company as the channel owner will be automatically subscribed to the channel.
     * They will not be able to unsubscribe.
     * Note that this option is authorized only if the channel visibility is set to 'company'.
     * The ability for a user to create a channel with this confidentiality is subject to user right managed by the Company Administrator.
     * The role "closed_channels_admin" must be assigned to the user who wants to create this type of channel.
     */
    CLOSED = "closed"
}

/**
 * Channel mode as received from server.
 * Then split into 2 information items: Channel visibility and + Channel confidentiality
 * Exclusively used by UCaaS for the moment.
 * In addition, the SDK does not include everything to do with the notion of organisation.
 * @internal
 */
export enum ChannelMode {
    /**
     * Channel with 'company' visibility + 'public' confidentiality
     */
    MY_COMPANY_PUBLIC = "company_public",

    /**
     * Channel with 'company' visibility + 'private' confidentiality
     */
    MY_COMPANY_PRIVATE = "company_private",

    /**
     * Channel for a company having the visibility closed.
     * A Channel owned by such company must never be accessed outside publishthe company/organisation.
     */
    MY_COMPANY_CLOSED = "company_closed",

    /**
     * Channel with 'all' visibility + 'public' confidentiality
     */
    ALL_PUBLIC = "all_public",

    /**
     * Channel with 'all' visibility + 'private' confidentiality
     */
    ALL_PRIVATE = "all_private",

    /**
     * Channel with 'organization' visibility + 'private' confidentiality
     */
    ORGANIZATION_PRIVATE = "organization_private",

    /**
     * Channel with 'organization' visibility + 'private' confidentiality
     */
    ORGANIZATION_PUBLIC = "organization_public"
}

/**
 * Channel user role
 * Each role gives access to different functionalities in Bubbles.
 */
export enum ChannelUserRole {
    /**
     * A user with the ‘none’ role is a user who is no longer a member of the channel following an unsubscription.
     * This role cannot be assigned directly to a user.
     * The use of this role is limited to the connected user and is set when it unsubscribe from the channel.
     * This is a temporary situation that makes it easier to re-subscribe to the same channel.
     */
    NONE = "none",

    /**
     * A user with the ‘owner’ role is the user who created the channel.
     * This user is unique and the only one authorized to manage the channel: updating general information
     * (category, name, topic, avatar), inviting or removing users from the channel, authorising or
     * denying members permission to publish posts.
     * He also has the right to publish posts in the channel.
     * This role cannot be assigned directly to a user when adding a user to the channel or modifying a channel member role.
     */
    OWNER = "owner",

    /**
     * A user with the 'member' role is a user who follows the channel.
     * The user is only permitted to view posts published in the channel; they are not allowed to publish any themselves.
     * This role can be assigned directly to a user when adding a user to the channel or modifying a channel member role.
     */
    MEMBER = "member",

    /**
     * A user with the role of publisher is a user who is a member of the channel and also has the right to publish posts in it.
     * This role can be assigned directly to a user when adding a user to the channel or modifying a channel member role.
     */
    PUBLISHER = "publisher"
}

/**
 * This interface defines specific user properties for a given channel.
 */
export interface ChannelUserInfos {
    /**
     * Indicates whether the user is currently being invited to join the channel.
     * If the data is not present, it means that there is no invitation in progress for this channel.
     * @readonly
     */
    invited?: boolean;

    /**
     * Indicates whether the user has subscribed to the channel.
     * A user who is invited to join a channel has also the "subscribed" state set to 'false'.
     * @readonly
     */
    subscribed?: boolean;

    /**
     * If the channel visibility is set to 'company' and the confidentiality to 'closed',
     * this indicates the date on which the user was automatically added to the channel.
     * Otherwise this corresponds to the date on which the user was invited to join the channel.
²     * @readonly
     */
    additionDate?: Date;

    /**
     * The user's role in the channel.
     * This corresponds to the type of channel affiliation that can be assigned to a new or an existing member.
     */
    userRole: ChannelUserRole;
}

export interface ChannelUser extends User {
    /**
     * The user information related to a channel.
     * Its invitation status, its subscription status (and date) and its role in the channel.
     */
    channelUser?: ChannelUserInfos;
}

/**
 * This interface defines the settings that can be sent to the setSettings channel API
 */
export interface ChannelSettings {
    /**
     * The channel name.
     * It must be unique.
     */
    name?: string;

    /**
     * The channel category.
     */
    category?: string;

    /**
     * The topic of the channel, i.e. a description explaining the purpose of the channel.
     */
    topic?: string;

    /**
     * Maximum number of posts to keep in the channel.
     * The maximum value is 100 and the default is 30.
     * If this value is reduced when updating the settings for a channel, any surplus posts stored will be deleted.
     */
    maxPost?: number;
}

export interface ChannelGetUsersOptions {
    /**
     * The position of the first found user to retrieve
     * If no offset is specified, the returned results will start from the first user found.
     * If the offset is greater than the total number of results, none are returned.
     */
    offset?: number;

    /**
     * The number of results to return.
     * Limit value must be in range [1..100]. Default value is 20.
     * If the value is not in the authorized range, the request will be rejected and an error logged.
     */
    limit?: number;

    /**
     * Filter the channel members to be searched according to their role (type of affiliation) within the channel.
     * If not specified, all members with the role 'owner', 'publisher' and 'member' will be searched.
     * Notes also that the role value 'none' is not managed and will be ignored.
     */
    userRoles?: ChannelUserRole[];

    /**
     * Filter on users invited to join the channel.
     * If not specified, no filter will be done on the invitation status.
     */
    invited?: boolean;
}

export interface ChannelSearchUsersOptions {
    /**
     * The position of the first found user to retrieve
     * If no offset is specified, the returned results will start from the first user found.
     * If the offset is greater than the total number of results, none are returned.
     */
    offset?: number;

    /**
     * The number of results to return.
     * Limit value must be in range [1..100]. Default value is 20.
     * If the value is not in the authorized range, the request will be rejected and an error logged.
     */
    limit?: number;
}

export interface ChannelAggregated {

    // WARNING (To Explain in Guide documentation)
    // Les posts du channel aggrégé sont réinitialisés et les 10 plus récents à nouveau récupérés dans les cas suivants
    // - Channel subscription (by invitation or by the connected user itself)
    // - Channel unsubcription
    // - Removal of the connected user from a channel
    // - Deletion of a channel
    // => A travers retrieveLatests();
    // page Index remis à 0 à la demande de UI (channelsList.component) sur reception de CURRENT_CHANNEL_UPDATE_EVENT
    // WARNING (To Explain in Guide documentation)

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    /**
     * Fixed name given to the aggregated channel.
     * @readonly
     */
    name: string;

    /**
     * Messages/Posts published in all channels to which the user is subscribed and which have already been retrieved.
     * @readonly
     */
    messages: any[];

    /**
     * Indicates whether a request to retrieve posts from the aggregate channel is in progress.
     * If so, another similar request will be ignored.
     * @readonly
     */
    isLoading: boolean;

    /**
     * Indicates whether a request to load publications has already been made for the channel.
     * If this is the case, the publications already loaded can be retrieved via API getPosts().
     * If not, API  loadPosts(...) must be first used for this purpose to retrieve the N most recent publications.
     * @readonly
     */
    loaded: boolean;

    /**
     * Indicates whether all posts from the aggregate channel have been retrieved.
     * @readonly
     */
    complete: boolean;

    // ----------------------------------------------------
    // INTERFACE API
    // ----------------------------------------------------

    /**
     * Get all posts already loaded (via loadPosts(...) API) for the aggregated channel.
     * If no post load operation has already been performed, an error is reported rather than an empty array to make the distinction.
     */
    getPosts(): ChannelPost[];

    /**
     * Load the specified number of additional posts from the aggregated channel. Defaut value is 10.
     * @param nbPosts - number of channel posts to load.
     */
    loadPosts(nbPosts?: number): Promise<ChannelPost[]>;
}

export interface Channel {

    // ----------------------------------------------------
    // INTERFACE DATA
    // ----------------------------------------------------

    // Channel settings & general data
    // -------------------------------

    /**
     * The channel unique identifier
     * @readonly
     */
    id: string;

    /**
     * Channel creation date. Automatically set when the channel is created.
     * @readonly
    */
    creationDate: Date;

    /**
     * The channel name.
     */
    name: string;

    /**
     * The channel category.
     */
    category: string;

    /**
     * The topic of the channel, i.e. a description explaining the purpose of the channel.
     */
    topic: string;

    /**
     * The customized channel avatar.
     */
    avatar?: string;

    /**
     * The channel avatar last modification date.
     * @readonly
     */
    lastAvatarUpdateDate?: Date | null;

    /**
     * The visibility of the channel, i.e. who the channel is intended for.
     * Once the channel is created, the visibility cannot be changed.
     * If this property is not specified, the visibility will be set to 'company'.
     * @readonly
     */
    visibility: ChannelVisibility;

    /**
     * In addition to the channel visibility, this allows to refine the channel's audience.
     * Once the channel is created, the confidentiality cannot be changed.
     * If this property is not specified, the visibility will be set to 'PUBLIC'.
     * @readonly
     */
    confidentiality: ChannelConfidentiality;

    /**
     * The number of channel members.
     * By member, we mean users who have already subscribed to the channel, not those who have a pending invitation.
     * @readonly
    */
    subscribersCount: number;

    // Data of the connected user in relation to the channel
    // -----------------------------------------------------

    /**
     * Information about the interaction between the connected user and this channel.
     */
    channelConnectedUser: ChannelUserInfos;

    // Channel Messages/Posts data
    // ---------------------------

    /**
     * Posts published in this channel that have already been retrieved.
     * @readonly
     */
    messages: any[];

    /**
     * Indicates whether a request to retrieve posts from the channel is in progress.
     * If so, another similar request will be ignored.
     * @readonly
     */
    isLoading: boolean;

    /**
     * Indicates whether a request to load publications has already been made for the channel.
     * If this is the case, the publications already loaded can be retrieved via API getPosts().
     * If not, API  loadPosts(...) must be first used for this purpose to retrieve the N most recent publications.
     * @readonly
     */
    loaded: boolean;

    /**
     * Indicates whether all posts from the channel have been retrieved.
     * @readonly
     */
    complete: boolean;

    // ----------------------------------------------------
    // INTERFACE API
    // ----------------------------------------------------

    /**
     * Allows ChannelEvents events to be sent.
     * Such events can be listened via channel.subscribe() API
     * @param name - the name of the event
     * @param data - optionally, data associated with the event to be sent (as an object)
     */
    sendEvent(name: ChannelEvents, data?: any): void;

    /**
     * Subscribe to channel updates, all events are of RBEvent<ChannelEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) A list of event names that the user wants to receive when subscribing (used as filter)
     */
    subscribe(callback: (event: RBEvent<ChannelEvents>) => any, eventNames?: ChannelEvents[]): Subscription;

    /**
     * Follow the channel.
     */
    follow(): Promise<void>;

    /**
     * Unfollow the channel.
     * At any time, a user can decide to no longer follow a channel's news by unsubscribing from it.
     * This operation is only possible with a channel whose confidentiality is set to ‘public’ or ‘private’, not with a 'closed' channel.
     * Note that in this case, the channel will not be removed from the channels visible to the user
     * to make it easier for them to subscribe to it again if necessary (e.g. in the event of incorrect handling).
     * However, the connected user will no longer have any role in this channel (it will be reset to "none").
     */
    unfollow(): Promise<void>;

    /**
     * Accept an invitation to join the channel.
     */
    acceptInvitation(): Promise<void>;

    /**
     * Decline invitation to join the channel.
     */
    declineInvitation(): Promise<void>;

    /**
     * Update the channel general settings
     * @param channelSettings - The settings to be updated in the channel
     */
    setSettings(channelSettings: ChannelSettings): Promise<Channel>;

    /**
     * Define a custom avatar for the channel.
     * This operation is only authorized for the owner of the channel.
     * @param base64AvatarImg - The image to be used as the channel's avatar
     * @param size - The size of the image (used as width and height) of the image (defaut value is 256).
     */
    setCustomAvatar(base64AvatarImg: string, size?: number): Promise<any>;

    /**
     * Remove the channel custom avatar
     * This operation is only authorized for the owner of the channel.
     */
    removeCustomAvatar(): Promise<any>;

    /**
     * Indicates whether the connected user is the owner of the channel.
     */
    isConnectedUserOwner(): boolean;

    /**
     * Returns the User who created the channel.
     */
    getOwner(): Promise<User>;

    /**
      * Retrieve the number of posts published in the channel.
      */
    getNumberOfPosts(): Promise<number>;

    /**
     * Get users who are members of the channel and/or users invited to join it.
     * If no option is specified, this API returns the first users who are members of the channel, whatever their role.
     * @param options - Options for refining the channel users to be retrieved.
     */
    getUsers(options?: ChannelGetUsersOptions): Promise<ChannelUser[]>;

    /**
     * Search for users matching the given name who are visible to the channel.
     * The scope of the possible users depends on the channel's configuration when it was created (settings 'visibility and 'confidentiality').
     * The search is done using the first name and last name of the user and provides channel affiliation information if any exists.
     * If a found user has not subscribed to the channel, it will be possible to add them to it (by invitation or automatically).
     * If a found user has subscribed to the channel, you can modify their role or even remove them from the channel.
     * The list of users found is returned sorted by name, in ascending order.
     * @param name - The search criterion based on the user's display name.
     * @param options - Options for refining the users to be provided.
     */
    searchUsers(name: string, options?: ChannelSearchUsersOptions): Promise<ChannelUser[]>;

    /**
     * Adds one or more users to the channel with the specified role, either simple member, or member with the right to publish.
     * Depending on the confidentiality of the channel defined when it was created, these users will either
     * be invited to join the channel or will be automatically subscribed to it.
     * @param users - The users to subscribe to the channel
     * @param role - The user's role.
     */
    addUsers(users: ChannelUser[], role: ChannelUserRole): Promise<void>;

    /**
     * Removes one or more user members from the channel.
     * @param users - The users to unsubscribe to the channel
     */
    removeUsers(users: ChannelUser[]): Promise<void>;

    /**
     * Update the role in the channel of a user who is a member of the channel or invited to join it.
     * @param user - The user whose role will be changed.
     * @param role - The user's new role.
     */
    updateUserRole(user: ChannelUser, role: ChannelUserRole): Promise<void>;

    /**
     * Delete the channel.
     * All channel members will be automatically unsubscribed.
     */
    delete(): Promise<void>;

    /**
     * Get all posts already loaded (via loadPosts(...) API) for the channel.
     * If no post load operation has already been performed, an error is reported rather than an empty array to make the distinction.
     */
    getPosts(): ChannelPost[];

    /**
     * Load the specified number of additional posts from the channel. Defaut value is 10.
     * @param nbPosts - number of channel posts to load.
     */
    loadPosts(nbPosts?: number): Promise<ChannelPost[]>;

    /**
     * Searches for posts in the channel based on their publication date.
     * If before and after dates are specified, then posts published within that time interval will be searched for.
     * If only a 'before date' is specified, then posts prior to the specified timestamp will be searched for.
     * If only a 'after date' is specified, then posts after to the specified timestamp will be searched for.
     * Please note that retrieved posts will not be considered as loaded.
     * They may therefore not appear in the list of posts retrieved via the getPosts() API.
     * This feature should be considered as a snapshot of posts published between two dates.
     * @param nbPosts - Number of publications to return in results
     * @param beforeDate - The publication must be prior to the specified timestamp (ISO 8601 format)
     * @param afterDate - The publication must occur after the specified timestamp (ISO 8601 format).
     */
    searchPosts(nbPosts: number, beforeDate: Date, afterDate: Date): Promise<ChannelMessage[]>;

    /**
     * Publish a new post or modify an existing post in the channel
     * @param post - Information contained in the post to add or update in the channel.
     */
    publish(post: ChannelPostPublication): Promise<any>;

    /**
     * Method to return a simplified JSON representation of the Channel object
     */
    toJSON(): any;

}

/**
* @internal
*/
export class ChannelNewsFeed {
    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------
    public messages: any[] = [];
    public loaded: boolean = false;
    public isLoading: boolean = false;
    public complete: boolean = false;

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------
    public pageIndex: number = 0;
}

/**
* @internal
*/
export class ChannelAggregatedRB extends ChannelNewsFeed implements ChannelAggregated {
    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------
    public name: string = "Aggregated channel";

    // ----------------------------------------------------
    // CHANNEL INSTANCE LIFE CYCLE
    // ----------------------------------------------------

    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private channelService?: any = MainService.getInstance().inject<ChannelService>(CHANNEL_SVC);
    private contactService?: any = MainService.getInstance().inject<ContactService>(CONTACT_SVC);
    private documentService?: any = MainService.getInstance().inject<FileDocumentService>(FILEDOCUMENT_SVC);

    // ----------------------------------------------------
    // AGGREGATED CHANNEL PUBLIC API (INCLUDING SDK)
    // ----------------------------------------------------

    // Aggregated Channel Posts
    // ---------------------------

    /**
     * Get all posts already loaded (via loadPosts(...) API) for the aggregated channel.
     * If no post load operation has already been performed, an error is reported rather than an empty array to make the distinction.
     */
    public getPosts(): ChannelPost[]
    {
        if (!this.loaded) {
            throw new Error(`[ChannelAggregated] No post load operation has been performed yet.`);
        }
        return this.messages;
    }

    /**
     * Load the specified number of additional posts from the aggregated channel. Defaut value is 10.
     * @param nbPosts - number of channel posts to load.
     */
    public async loadPosts(nbPosts?: number): Promise<ChannelPost[]> {
        try {

            // Check requested nb of additional call logs
            if (nbPosts && (nbPosts <= 0 || nbPosts > NB_POSTS_TO_LOAD_MAX)) {
                throw new Error(`[ChannelAggregated] Value of 'nbPosts' parameter (${nbPosts}) is not valid !`);
            }

            if (this.isLoading) {
                throw new Error(`[ChannelAggregated] Request ignored, post loading operation already in progress for the aggregated channel`);
            }

            if (this.complete) {
                this.logger.info(`[ChannelAggregated] Request ignored, All channel posts have already been loaded for the aggregated channel.`);
                return [];
            }

            const nbPostsToLoad: number = nbPosts ? nbPosts : NB_POSTS_TO_LOAD_DEF;
            return await this.channelService?.loadPosts(this, nbPostsToLoad);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'loadPosts');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }
}

/**
 * @internal
 */
export class ChannelRB extends ChannelNewsFeed implements Channel {

    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------

    // Channel settings & general data
    // -------------------------------
    public id: string = "";
    public creationDate: Date = null;

    /** The userd Id of the channel creator */
    public creatorId: string = "";

    public name: string = "";
    public category: string = "globalnews";
    public topic: string = "";

    /** Visibility and confidentiality (audience) of the channel */
    public visibility: ChannelVisibility;
    public confidentiality: ChannelConfidentiality;

    /** The customized channel avatar */
    public avatar: string = "";
    public lastAvatarUpdateDate: Date | null = null;

    public subscribersCount: number = 0;

    // Data of the connected user in relation to the channel
    // -----------------------------------------------------
    public channelConnectedUser: ChannelUserInfos = {
        invited: false,
        subscribed: false,
        additionDate: null,
        userRole: ChannelUserRole.NONE
    };

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------

    // Observables for events sent by a channel instance
    public rxSubject: Subject<RBEvent<ChannelEvents>> = new Subject<RBEvent<ChannelEvents>>();

    // Observables for inner events sent by a channel instance
    public innerRxSubject: Subject<RBEvent<ChannelInnerEvents>> = new Subject<RBEvent<ChannelInnerEvents>>();

    // Channel management (parameters, users...)
    // ------------------------------------------

    // The company Id of the user who created the channel
    public companyId: string = "";

    // The number of channel members + the number of invited users.
    public usersCount: number = 0;

    /**
     * !! WARNING !!
     * The ‘visibility’ property received from the server appears to no longer be used.
     * It has been renamed to 'visibilityPubSub' and replaced in use by the ‘mode’ data.
     * However, its definition and use are retained in the .model file to avoid problems on very old channels
     * and thus maintain compatibility.
     *
     * "private" : a « Pub » channel, only the owner may publish posts.<br/>
     *   Managed by owner, the only one who can add or remove users in a private channels.<br/>
     *   Can't be found by search.<br/>
     * "company" : « PubSub » channel (company users may join/leave)
     *   May be found by search for users in the same company.<br/>
     * "public"  : « PubSub » public channel.
     *   Only allowed users may create a "public" channel.
     *   May be found by search for all users.<br/>
     */
    private visibilityPubSub: string = "";

    /** The channel policy/behaviour based on 'visibility_confidentiality' */
    public mode: string = "";

    // Channel messages/posts management
    // ------------------------------------------
    public messagesCount: number = undefined;

    /*
     * channel mute/unmute status.
     * If muted, no alerts will be sent when a new post is published in the channel.
     */
    public muted: boolean = false;

    // ----------------------------------------------------
    // CHANNEL INSTANCE LIFE CYCLE
    // ----------------------------------------------------

    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private channelService?: any = MainService.getInstance().inject<ChannelService>(CHANNEL_SVC);
    private contactService?: any = MainService.getInstance().inject<ContactService>(CONTACT_SVC);
    private documentService?: any = MainService.getInstance().inject<FileDocumentService>(FILEDOCUMENT_SVC);
    // private sanitizer: DomSanitizer;

    protected constructor() {
        super();
    }

    public updateFromData(data: any) {
        this.id = data.id;
        this.name = data.name;
        this.topic = data.topic;
        this.category = data.category;

        this.creationDate = data.creationDate;

        this.lastAvatarUpdateDate = data.lastAvatarUpdateDate;
        const timestamp = this.lastAvatarUpdateDate ? "&ts=" + new Date(this.lastAvatarUpdateDate).getTime() : "";
        this.avatar = config.restServerUrl + "/api/channel-avatar/" + data.id + "?size=256" + timestamp;

        this.creatorId = data.creatorId;
        this.companyId = data.companyId;

        if (data.additionDate !== undefined) {
            this.channelConnectedUser.additionDate = data.additionDate;
        }
        else {
            // PMA: 'else' instruction is ONLY temporary code which have to be removed once server update is really available on Prod (mid may 2022 - 103 ?)
            //      This has been done to 'additionDate' always available even if with the old wrong information
            // 20/06/25: I don't know if this point is still relevant.
            this.channelConnectedUser.additionDate = data.creationDate;
        }

        this.usersCount = data.usersCount;
        this.subscribersCount = data.subscribers_count;

        if (data.type !== undefined) {
            this.channelConnectedUser.userRole = data.type;
        }

        if (data.subscribed !== undefined) {
            this.channelConnectedUser.subscribed = data.subscribed;
        }
        else {
            this.channelConnectedUser.subscribed = false;
        }

        if (data.invited !== undefined) { this.channelConnectedUser.invited = data.invited; }

        this.muted = data.mute;
        this.visibilityPubSub = data.visibility;
        this.mode = data.mode;
        if (this.mode) {
            this.visibility = this.mode.split('_')[0] as ChannelVisibility;
            this.confidentiality = this.mode.split('_')[1] as ChannelConfidentiality;
        }

        if (!this.mode) {
            // Should no more happen !
            // The ‘visibility’ property received from the server appears to no longer be used.
            // It has been replaced in use by the ‘mode’ data.
            switch (this.visibilityPubSub) {
                case "company":
                    this.visibility = ChannelVisibility.MY_COMPANY;
                    this.confidentiality = ChannelConfidentiality.PUBLIC;
                    this.mode = ChannelMode.MY_COMPANY_PUBLIC;
                    break;

                case "public":
                    this.visibility = ChannelVisibility.ALL_USERS;
                    this.confidentiality = ChannelConfidentiality.PUBLIC;
                    this.mode = ChannelMode.ALL_PUBLIC;
                    break;

                case "private":
                    this.visibility = ChannelVisibility.MY_COMPANY;
                    this.confidentiality = ChannelConfidentiality.PRIVATE;
                    this.mode = ChannelMode.MY_COMPANY_PRIVATE;
                    break;

                default: break;
            }
        }
    }

    public static createEmpty(): ChannelRB {
        const channel = new ChannelRB();
        return channel;
    }

    public static createFromData(data: any): ChannelRB {
        const channel: ChannelRB = new ChannelRB();
        channel.updateFromData(data);

        if (!channel.category) { channel.category = "globalnews"; }
        return channel;
    }

    // ----------------------------------------------------
    // CHANNEL INSTANCE PUBLIC API NOT USED BY SDK
    // ----------------------------------------------------

    public isNotMember() { return (this.channelConnectedUser.userRole = ChannelUserRole.NONE); }

    public isMember() { return this.channelConnectedUser.userRole === ChannelUserRole.MEMBER; }

    public isPublisher() {
        return (this.channelConnectedUser.subscribed
                && (this.channelConnectedUser.userRole === ChannelUserRole.OWNER || this.channelConnectedUser.userRole === ChannelUserRole.PUBLISHER));
    }

    public getAvatarSrc() { return (this.lastAvatarUpdateDate) ? this.avatar : "/assets/skins/rainbow/images/channels/default_channel_avatar.png"; }

    // check if a JSON object is empty (equal to {})
    protected isChannelObjectEmpty(obj: any): boolean {
        return Object.keys(obj).length === 0;
    }

    // ----------------------------------------------------
    // CHANNEL INSTANCE PUBLIC API (INCLUDING SDK)
    // ----------------------------------------------------

    //  OBSERVABLE
    // ----------------------------------------------------
    public sendEvent(name: ChannelEvents, data: any = null) {
        // Local debug only: this.logger.warn(`[Channel] sendEvent -- name: ${name}`);
        this.rxSubject.next(RBEvent.create<ChannelEvents, any>(name, data));
    }

    public subscribe(callback: (event: RBEvent<ChannelEvents>) => any, eventNames?: ChannelEvents | ChannelEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<ChannelEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<ChannelEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[Channel] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendInnerEvent(name: ChannelInnerEvents, data: any = null) {
        // PMA DEBUG ONLY
        // this.logger.error(`[Channel] sendInnerEvent -- name: ${name}`);
        this.innerRxSubject.next(RBEvent.create<ChannelInnerEvents, any>(name, data));
    }

    public subscribeToInnerEvents(handler: (event: RBEvent<ChannelInnerEvents>) => any): Subscription {
        return this.innerRxSubject.subscribe(handler);
    }

    // CHANNEL SUBSCRIPTION
    // ----------------------

    public async follow(): Promise<void> {
        await this.channelService?.subscribeToChannel(this);
    }

    public async unfollow(): Promise<void> {
        try {
            // Preliminary data integrity check
            if (this.confidentiality !== ChannelConfidentiality.PUBLIC && this.confidentiality !== ChannelConfidentiality.PRIVATE)
            {
                throw new Error(`[Channel] Not possible to unsubscribe from a channel with confidentiality ${this.confidentiality}`);
            }

            await this.channelService?.unsubscribeToChannel(this);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'unsubscribe');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    /**
     * Accept an invitation to join the channel.
     */
    public async acceptInvitation(): Promise<void> {
        await this.channelService?.acceptInvitation(this);
    }

    /**
     * Decline invitation to join the channel.
     */
    public async declineInvitation(): Promise<void> {
        await this.channelService?.declineInvitation(this);
    }

    // CHANNEL PROPERTIES
    // ----------------------

    /**
     * Update the channel general settings.
     * This operation is only authorized for the owner of the channel.
     * @param channelSettings - The settings to be updated in the channel
     */
    public async setSettings(channelSettings: ChannelSettings): Promise<Channel> {
        try {
            // Check that the user is the user of the channel to allow to update the channel general settings
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }
            if (this.isChannelObjectEmpty(channelSettings)) {
                throw new Error(`[Channel] 'setSettings' API called with an empty object parameter`);
            }

            return this.channelService?.setSettings(this, channelSettings);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setSettings');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async setCustomAvatar(base64AvatarImg: string, size: number = 256): Promise<any> {
        try {
            // Check that the user is the user of the channel to allow to set the channel avatar
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }
            return this.channelService?.uploadChannelAvatar(this, base64AvatarImg, size);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setCustomAvatar');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async removeCustomAvatar(): Promise<any> {
        try {
            // Check that the user is the user of the channel to allow the avatar to be removed
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }
            return this.channelService?.deleteChannelAvatar(this);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeCustomAvatar');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public get avatarSrc(): string {
        return (this.lastAvatarUpdateDate) ? this.avatar : "/assets/skins/rainbow/images/channels/default_channel_avatar.png";
    }

    public async delete(): Promise<void> {
        try {
            // Check that the user is the user of the channel to allow the deletion
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }
            return this.channelService?.deleteChannel(this);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'delete');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public isConnectedUserOwner(): boolean {
        return (this.channelConnectedUser.userRole === ChannelUserRole.OWNER);
    }

    public async getOwner(): Promise<User> {
        const owner: User = await this.contactService.getContactByDBId(this.creatorId);
        return owner;
    }

    public async getNumberOfPosts(): Promise<number> {
        if (this.messagesCount !== undefined) {
            return this.messagesCount;
        }

        // Requests only the number of posts already published in the channel.
        // For the request sent to server,  parameter 'maxMessages' set to 0 and no date specified.
        await this.channelService?.getChannelPosts(this.id, 0);
        return this.messagesCount;
    }

    // CHANNEL USERS
    // ----------------------

    public async getUsers(options?: ChannelGetUsersOptions): Promise<ChannelUser[]> {
        try {
            // Set default value for some options.
            const getOptions: ChannelGetUsersOptions = {
                limit: NB_USERS_TO_GET_DEF
            };

            // NOTE: If an error is detected in the getUsers parameters, an exception is thrown and the process stops.
            if (options) {
                // Check 'limit' option
                if (options?.limit) {
                    if ((options?.limit <= 0) || (options?.limit > NB_USERS_TO_GET_MAX)) {
                        throw new Error(`[Channel] Value of 'limit' option (${options.limit}) is not valid !`);
                    }
                    getOptions.limit = options?.limit;
                }

                // Check 'offset' option
                if (options?.offset) {
                    if (options?.offset <= 0) {
                        throw new Error(`[Channel] Value of 'offset' option (${options.offset}) is not valid !`);
                    }
                    getOptions.offset = options?.offset;
                }

                // Check 'userRoles' option
                if (options?.userRoles) {
                    // const userRoles: ChannelUserRole[] = [...new Set(options?.userRoles)];
                    getOptions.userRoles = options?.userRoles;
                }

                // Check 'invited' option
                if (options?.invited) {
                    getOptions.invited = options?.invited;
                }
            }

            const usersResult: ChannelGetUsersResults = await this.channelService?.getChannelUsers(this, getOptions);
            return usersResult.ChannelUsers;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getUsers');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async searchUsers(name: string, options?: ChannelSearchUsersOptions): Promise<ChannelUser[]> {
        try {
            // Set default value for some options.
            const getOptions: ChannelSearchUsersOptions = {
                offset: 0,
                limit: NB_USERS_TO_GET_DEF
            };

            // NOTE: If an error is detected in the searchUsers parameters, an exception is thrown and the process stops.
            // No pattern search specified, not useful to continue !
            if (!name?.length) {
                throw new Error(`[Channel] No search name pattern specified !`);
            }

            if (options) {
                // Check 'limit' option
                if (options?.limit) {
                    if ((options?.limit <= 0) || (options?.limit > NB_USERS_TO_GET_MAX)) {
                        throw new Error(`[Channel] Value of 'limit' option (${options.limit}) is not valid !`);
                    }
                    getOptions.limit = options?.limit;
                }

                // Check 'offset' option
                if (options?.offset) {
                    if (options?.offset <= 0) {
                        throw new Error(`[Channel] Value of 'offset' option (${options.offset}) is not valid !`);
                    }
                    getOptions.offset = options?.offset;
                }
            }

            const usersResult: ChannelUser[] = await this.channelService?.searchUsersByName(this, name, getOptions);
            return usersResult;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchUsers');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async addUsers(users: ChannelUser[], role: ChannelUserRole): Promise<void> {
        try {
            // Check that the user is the user of the channel to allow the deletion
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }

            // Check if an invalid new user role has been specified, i.e a value other than 'member' or 'publisher'
            if (role !== ChannelUserRole.MEMBER && role !== ChannelUserRole.PUBLISHER) {
                throw new Error("[Channel] Invalid channel role for the user(s) to add !");
            }

            return this.channelService?.updateUsers(this, users, role);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'addUsers');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async removeUsers(users: ChannelUser[]): Promise<void> {
        try {
            // Check that the user is the user of the channel to allow the deletion
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }

            return this.channelService?.updateUsers(this, users, ChannelUserRole.NONE);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeUsers');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async updateUserRole(user: ChannelUser, role: ChannelUserRole): Promise<void> {
        try {
            // Check that the user is the user of the channel to allow the update of a channel user role
            if (!this.isConnectedUserOwner()) {
                throw new Error(`[Channel] Only the channel owner is authorized to perform this operation.`);
            }

            // Check if an invalid new user role has been specified, i.e a value other than 'member' or 'publisher'
            if (role !== ChannelUserRole.MEMBER && role !== ChannelUserRole.PUBLISHER) {
                throw new Error("[Channel] Invalid channel role for the user !");
            }

            return this.channelService?.updateUsers(this, [user], role);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateUserRole');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    // CHANNEL POSTS
    // ----------------------

    public getPosts(): ChannelPost[]
    {
        if (!this.loaded) {
            throw new Error(`[Channel] No post load operation has been performed yet.`);
        }

        return this.messages;
    }

    /**
     * Load the specified number of posts from the channel. Defaut value is 10.
     * @param nbPosts - number of channel posts to load.
     */
    public async loadPosts(nbPosts?: number): Promise<ChannelPost[]> {
        try {

            // Check requested nb of additional call logs
            if (nbPosts && (nbPosts <= 0 || nbPosts > NB_POSTS_TO_LOAD_MAX)) {
                throw new Error(`[Channel] Value of 'nbPosts' parameter (${nbPosts}) is not valid !`);
            }

            if (this.isLoading) {
                throw new Error(`[Channel] Request ignored, post loading operation already in progress`);
            }

            if (this.complete) {
                this.logger.info(`[Channel] Request ignored, All channel posts have already been loaded.`);
                return [];
            }

            const nbPostsToLoad: number = nbPosts ? nbPosts : NB_POSTS_TO_LOAD_DEF;
            return await this.channelService?.loadPosts(this, nbPostsToLoad);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPosts');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    public async searchPosts(nbPosts: number, beforeDate: Date, afterDate: Date): Promise<ChannelMessage[]> {
        try {
            // Check requested nb of additional call logs
            if (nbPosts && (nbPosts <= 0 || nbPosts > NB_POSTS_TO_LOAD_MAX)) {
                throw new Error(`[Channel] Value of 'nbPosts' parameter (${nbPosts}) is not valid !`);
            }

            // Seems to be not useful as the retirved posts are not stored in channel cache
            // So no conflict in the internal management of channel posts.
            // if (this.isLoading) {
            //     throw new Error(`[Channel] Request ignored, post search operation already in progress`);
            // }

            const nbPostsToLoad: number = nbPosts ? nbPosts : NB_POSTS_TO_LOAD_DEF;
            return await this.channelService?.searchPosts(this, nbPostsToLoad, beforeDate, afterDate);
            // public async searchPosts(nbPosts: number, beforeDate: Date, afterDate: Date): Promise<ChannelMessage[]> {

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchPosts');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    /**
     * Publish a new post or modify an existing post in the channel
     */
    public async publish(post: ChannelPostPublication): Promise<any> {
        try {
            // Check that the user is allow to pusblish.
            if (!this.isPublisher()) {
                throw new Error(`[Channel] Only the channel owner or a user with "publisher" role is authorized to publish a post.`);
            }

            // Checking the documents provided

            // Check  that the number of documents provided for images and attachments does not exceed the authorized limit.
            if (post?.images?.length > CHANNEL_POST_MAX_IMAGES) {
                this.logger.error(`[Channel] The number of images inserted in the post has exceeded the maximum allowed`);
            }

            if (post?.attachments?.length > CHANNEL_POST_MAX_ATTACHMENTS) {
                this.logger.error(`[Channel] The number of files attached in the post has exceeded the maximum allowed`);
            }

            // Ensure that the documents provided in 'post.images' are in image format.
            // If not the case, the publication will be rejected.
            if (post?.images) {
                for (const image of post.images) {
                    try {
                        const document: FileDocument = await this.documentService.getDocumentById(image.id);
                        if (!(document.fileDescriptor?.typeMIME && document.fileDescriptor.typeMIME.startsWith('image'))) {
                            throw new Error(`[Channel] Document id: ${image.id} is not an image`);
                        }
                    }
                    catch (error) {
                        const errorInfo = await this.errorHelperService.getErrorInfo(error, 'searchPosts');
                        this.logger.error(errorInfo.message);
                        throw errorInfo.error;
                    }
                }
            }

            // For security, check the message content
            // let sanitizedMessage: string = "";
            // switch (post.message) {
            //     case ChannelPostMessageFormat.MARKDOWN:
            //         sanitizedMessage = this.rainbowService.showdownConverter.makeHtml(post.message);
            //         break;

            //     case ChannelPostMessageFormat.HTML:
            //         sanitizedMessage = post.message;
            //         break;

            //     default:
            //         break;
            // }
            // sanitizedMessage = this.sanitizer.sanitize(SecurityContext.HTML, sanitizedMessage);

            return await this.channelService.publishToChannel(this.id, post?.id, post.format, post.message, post.title,
                post.images, post.video, post.attachments);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'publish');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    // ----------------------------------------------------
    //  CHANNEL INSTANCE DEBUG (MAY BE USED BY SDK)
    // ----------------------------------------------------

    public toJSON(): any {
        // JSON output for channel information
        const json: any = {
            id: this.id,
            name: this.name,
            category: this.category,
            topic: this.topic,
            visibility: this.visibility,
            confidentiality: this.confidentiality,
            subscribersCount: this.subscribersCount,
        };

        if (this.creationDate) { json.creationDate = this.creationDate; }
        if (this.creatorId) { json.creatorId = this.creatorId; }
        if (this.avatar) { json.avatar = this.avatar; }
        if (this.lastAvatarUpdateDate) { json.lastAvatarUpdateDate = this.lastAvatarUpdateDate; }

        if (this.channelConnectedUser) {
            json.connectedUser = {
                userRole: this.channelConnectedUser.userRole,
                subscribed: this.channelConnectedUser.subscribed,
                invited: this.channelConnectedUser.invited,
                additionDate: this.channelConnectedUser.additionDate
            };
        }
        else json.channelConnectedUser = 'UNDEFINED';

        // const owner: User = await this.getOwner();
        // if (owner) {
        //     json.owner = {
        //         displayName: owner.displayName,
        //         companyName: owner.companyInfo.name
        //     };
        // }

        return json;
    }
}
